import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard, currency } from "@/components/Primitives";
import { Plus, ExternalLink, Inbox, LayoutGrid, Search, GripVertical, BadgeCheck } from "lucide-react";
import { AgentContact } from "@/components/AgentContact";

const STAGE_LABEL = { coming_soon: "Coming Soon", for_sale: "For Sale", under_contract: "Under Contract", sold: "Sold" };
const STAGE_TONE = { coming_soon: "amber", for_sale: "green", under_contract: "orange", sold: "emerald" };

export default function ListingManager() {
  const { user } = useAuth();
  const canWrite = user.access?.listings ? user.access.listings === "write" : (user.role === "admin" || user.role === "marketing_manager");
  const canEdit = true; // agents can open/edit listings assigned to them (list is already scoped to their own)
  const navigate = useNavigate();
  const [tab, setTab] = useState("properties");
  const [props, setProps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    const [p, l, s] = await Promise.all([
      api.get("/lm/properties"), api.get("/lm/leads"), api.get("/lm/analytics"),
    ]);
    setProps(p.data); setLeads(l.data); setStats(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createProperty = async () => {
    try {
      const { data } = await api.post("/lm/properties", { address_line: "", status: "draft" });
      navigate(`/listings/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reorder = async (fromId, toId) => {
    if (!fromId || fromId === toId) return;
    const arr = [...props];
    const from = arr.findIndex((p) => p.id === fromId);
    const to = arr.findIndex((p) => p.id === toId);
    if (from < 0 || to < 0) return;
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    setProps(arr);
    try { await api.post("/lm/properties/reorder", { ordered_ids: arr.map((p) => p.id) }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save order"); load(); }
  };

  const soldCount = props.filter((p) => p.listing_stage === "sold").length;
  const visible = props.filter((p) => (tab === "sold") === (p.listing_stage === "sold"));
  const canReorder = canWrite && !query.trim() && tab === "properties";

  const StatusBadge = ({ tone, children }) => (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${{
      green: "bg-[#E7F0EA] text-[#2E5C47]", amber: "bg-[#FBF2DE] text-[#B07D14]",
      emerald: "bg-[#1F3C88] text-white", gray: "bg-[#F0EFEC] text-[#575755]",
    }[tone]}`}>{children}</span>
  );

  return (
    <div data-testid="listings-page">
      <PageHeader
        title="Property Websites"
        subtitle="Build a stunning marketing website for every listing, capture leads, and track engagement — all shareable with one link."
        action={canWrite && tab === "properties" && (
          <button data-testid="add-listing-button" onClick={createProperty} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312] transition-colors duration-200">
            <Plus className="w-4 h-4" /> New Listing
          </button>
        )}
      />

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard index={0} label="Listings" value={stats.total} sub={`${stats.published} published`} />
          <StatCard index={1} label="Leads" value={stats.leads} sub="Captured inquiries" />
          <StatCard index={2} label="Page Views" value={stats.views} sub="Across all sites" />
          <StatCard index={3} label="Drafts" value={stats.total - stats.published} sub="Not yet published" />
        </div>
      )}

      <div className="flex items-center gap-1 mb-6 border-b border-[#E5E5E2]">
        {[["properties", "Properties", LayoutGrid], ["sold", `Sold (${soldCount})`, BadgeCheck], ["leads", "Leads", Inbox]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`lm-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors duration-200 ${tab === k ? "border-[#F7941D] text-[#1F3C88]" : "border-transparent text-[#575755] hover:text-[#1F3C88]"}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {(tab === "properties" || tab === "sold") && (
        <>
          <div className="relative mb-6 max-w-md">
            <Search className="w-4 h-4 text-[#8a8a86] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="listing-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by address, city, state or ZIP…"
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#E5E5E2] rounded-full text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none"
            />
          </div>
          {canReorder && visible.length > 1 && (
            <p className="text-xs text-[#8a8a86] mb-4 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" /> Drag listings to set the order they appear on your agent page.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible
              .filter((p) => {
                const q = query.trim().toLowerCase();
                if (!q) return true;
                return [p.address_line, p.city, p.state, p.zip_code].filter(Boolean).join(" ").toLowerCase().includes(q);
              })
              .map((p, i) => (
            <div key={p.id} data-testid={`listing-card-${p.id}`}
              draggable={canReorder}
              onDragStart={() => setDragId(p.id)}
              onDragEnd={() => setDragId(null)}
              onDragOver={(e) => { if (canReorder && dragId) e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (canReorder) reorder(dragId, p.id); setDragId(null); }}
              onClick={() => canEdit && navigate(`/listings/${p.id}`)}
              className={`relative bg-white border border-[#E5E5E2] rounded-sm overflow-hidden animate-fade-up ${canEdit ? "cursor-pointer hover:border-[#F7941D]" : ""} ${dragId === p.id ? "opacity-40" : ""} transition-colors duration-200`} style={{ animationDelay: `${i * 50}ms` }}>
              {canReorder && <span data-testid={`listing-drag-${p.id}`} onClick={(e) => e.stopPropagation()} className="absolute top-3 right-3 z-10 bg-white/90 border border-[#E5E5E2] rounded-sm p-1 text-[#8a8a86] cursor-grab active:cursor-grabbing" title="Drag to reorder"><GripVertical className="w-4 h-4" /></span>}
              <div className="relative h-44 bg-[#F0EFEC]">
                {p.hero_photo ? <img src={p.hero_photo} alt={p.address_line} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-xs text-[#B9B5AC] uppercase tracking-widest">No Image</div>}
                <div className="absolute top-3 left-3 flex gap-2">
                  <StatusBadge tone={STAGE_TONE[p.listing_stage]}>{STAGE_LABEL[p.listing_stage]}</StatusBadge>
                  <StatusBadge tone={p.status === "published" ? "green" : "gray"}>{p.status === "published" ? "Live" : "Draft"}</StatusBadge>
                </div>
              </div>
              <div className="p-5">
                <div className="font-serif text-2xl text-[#1F3C88]">{currency(p.listing_stage === "sold" && p.sold_price ? p.sold_price : p.price)}</div>
                {p.listing_stage === "sold" && (
                  <div data-testid={`sold-info-${p.id}`} className="text-xs text-[#2E5C47] font-medium mt-1">
                    {p.sold_price ? "Sold price" : "List price"}{p.sold_date ? ` · Sold ${new Date(`${p.sold_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                  </div>
                )}
                <div className="text-sm text-[#575755] mt-1">{p.address_line || "Untitled Listing"}</div>
                <div className="text-xs text-[#8a8a86]">{[p.city, p.address_line ? p.state : ""].filter(Boolean).join(", ")}</div>
                <div className="mt-4 pt-4 border-t border-[#F0EFEC] flex items-center justify-between text-xs text-[#575755]">
                  <span>{p.beds}bd · {p.baths}ba · {p.sqft?.toLocaleString()} sqft</span>
                  <span>{p.leads_count} leads · {p.views_count} views</span>
                </div>
                {p.agent_profile && <div className="mt-3 pt-3 border-t border-[#F0EFEC]"><AgentContact profile={p.agent_profile} /></div>}
                {p.status === "published" && (
                  <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`view-site-${p.id}`} onClick={(e) => e.stopPropagation()}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#F7941D] hover:text-[#E08312]">
                    <ExternalLink className="w-3.5 h-3.5" /> View live site
                  </a>
                )}
              </div>
            </div>
          ))}
            {visible.length === 0 && <div data-testid="listings-empty" className="text-sm text-[#8a8a86]">{tab === "sold" ? "No sold listings yet." : "No listings yet."}</div>}
          </div>
        </>
      )}

      {tab === "leads" && (
        <div className="bg-white border border-[#E5E5E2] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E5E5E2] text-left text-xs tracking-[0.12em] uppercase text-[#575755]">
                <th className="px-6 py-3 font-semibold">Name</th><th className="px-6 py-3 font-semibold">Contact</th>
                <th className="px-6 py-3 font-semibold">Property</th><th className="px-6 py-3 font-semibold">Comment</th><th className="px-6 py-3 font-semibold">Received</th>
              </tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[#F0EFEC] hover:bg-[#FBFAF8]">
                    <td className="px-6 py-3 font-medium text-[#0A0A0A]">{l.first_name} {l.last_name}</td>
                    <td className="px-6 py-3 text-[#575755]">{l.email}<div className="text-xs">{l.phone}</div></td>
                    <td className="px-6 py-3 text-[#575755]">{l.property_address}</td>
                    <td className="px-6 py-3 text-[#575755] max-w-xs truncate">{l.comment || "—"}</td>
                    <td className="px-6 py-3 text-[#8a8a86] text-xs">{(l.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-[#8a8a86]">No leads captured yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
